import React, {
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { motion } from "framer-motion";
import { envConfig } from "../constants";
import { useSupabase } from "../supabase/supabase";
import fetchWithRetry from "../utils/fetch-with-retry";
import { retrySupabaseOnFail } from "../utils/retry-supabase";

// Create a context
export const RemainingCreditsContext = React.createContext<
  { usage: RemainingUsage } & { limits: GeneralLimits } & {
    isLoading: boolean;
  } & { refreshCredits: () => Promise<void> } & {
    incrementOfflineWords?: (words: number) => void;
  }
>({
  usage: {} as RemainingUsage,
  limits: {} as GeneralLimits,
  isLoading: true,
  refreshCredits: async () => {},
});

type RemainingUsage = {
  ownerId: string;
  ownerName: string;
  consumedWords: number;
  remainingWords: number;
  totalWords: number;
  cycleStartDate: string;
  cycleEndDate: string;
  planName: string;
  isUnlimited: boolean;
  stats?: {
    wpm: number;
    minutesSaved: number;
    totalWordsAllTime: number;
  };
};

type GeneralLimits = {
  remainingWorkspaces: number;
  remainingTeamMembers: number;
  maxWorkspaces: number;
  maxTeamMembers: number;
  planType: "UNLIMITED" | "BUSINESS" | "FREE";
  didRedeemTrial: boolean;
  wordLimit: number;
  isUnlimited: boolean;
  isAppSumo: boolean;
  forceLocalModel: "everyone" | "appsumo" | "none";
};

// Constants for offline mode
const OFFLINE_WORD_LIMIT = 2000;
const LOCAL_STORAGE_KEY = "writevoice_offline_credits";

// Provider component that wraps your app and makes an API call to fetch remaining credits
export const RemainingCreditsProvider = ({
  children,
}: {
  children: ReactElement;
}) => {
  const [ownerGeneralLimits, setOwnerGeneralLimits] = useState<GeneralLimits>();
  // usestate for remainingUsage
  const [remainingUsage, setRemainingUsage] = useState<RemainingUsage>();

  const supabase = useSupabase();
  // isLoading - only true on first load ever
  const [isLoading, setIsLoading] = useState(true);

  const isMountedRef = useRef<boolean>(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load offline credits from localStorage
  const loadOfflineCredits = () => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    if (stored) {
      try {
        const data = JSON.parse(stored);
        // Reset if it's a new month
        if (data.month !== currentMonth || data.year !== currentYear) {
          return resetOfflineCredits();
        }
        return data;
      } catch (error) {
        console.error("Error parsing offline credits:", error);
        return resetOfflineCredits();
      }
    } else {
      return resetOfflineCredits();
    }
  };

  // Reset offline credits for new month
  const resetOfflineCredits = () => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const startDate = new Date(currentYear, currentMonth, 1).toISOString();
    const endDate = new Date(currentYear, currentMonth + 1, 0).toISOString();

    const offlineData = {
      consumedWords: 0,
      remainingWords: OFFLINE_WORD_LIMIT,
      totalWords: OFFLINE_WORD_LIMIT,
      month: currentMonth,
      year: currentYear,
      cycleStartDate: startDate,
      cycleEndDate: endDate,
    };

    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(offlineData));
    return offlineData;
  };

  // Increment offline word count
  const incrementOfflineWords = (words: number) => {
    const offlineData = loadOfflineCredits();
    const newConsumed = offlineData.consumedWords + words;
    const newRemaining = Math.max(0, OFFLINE_WORD_LIMIT - newConsumed);

    const updatedData = {
      ...offlineData,
      consumedWords: newConsumed,
      remainingWords: newRemaining,
    };

    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedData));

    // Update state for offline mode
    setRemainingUsage({
      ownerId: "offline",
      ownerName: "Offline User",
      consumedWords: newConsumed,
      remainingWords: newRemaining,
      totalWords: OFFLINE_WORD_LIMIT,
      cycleStartDate: offlineData.cycleStartDate,
      cycleEndDate: offlineData.cycleEndDate,
      planName: "FREE",
      isUnlimited: false,
      stats: {
        wpm: 150,
        minutesSaved: Math.round(newConsumed / 30),
        totalWordsAllTime: newConsumed,
      },
    });
  };

  // Simple fetch function
  const fetchCredits = async () => {
    if (!isMountedRef.current) return;

    try {
      const {
        data: { session },
      } = await retrySupabaseOnFail(() => supabase.auth.getSession());

      // If no session, use offline mode
      if (!session?.access_token) {
        const offlineData = loadOfflineCredits();

        setRemainingUsage({
          ownerId: "offline",
          ownerName: "Offline User",
          consumedWords: offlineData.consumedWords,
          remainingWords: offlineData.remainingWords,
          totalWords: offlineData.totalWords,
          cycleStartDate: offlineData.cycleStartDate,
          cycleEndDate: offlineData.cycleEndDate,
          planName: "FREE",
          isUnlimited: false,
          stats: {
            wpm: 150,
            minutesSaved: Math.round(offlineData.consumedWords / 30),
            totalWordsAllTime: offlineData.consumedWords,
          },
        });

        setOwnerGeneralLimits({
          remainingWorkspaces: 1,
          remainingTeamMembers: 1,
          maxWorkspaces: 1,
          maxTeamMembers: 1,
          planType: "FREE",
          didRedeemTrial: false,
          wordLimit: OFFLINE_WORD_LIMIT,
          isUnlimited: false,
          isAppSumo: false,
          forceLocalModel: "none",
        });

        if (isLoading) {
          setIsLoading(false);
        }
        return;
      }

      const res = await getRemainingCredits({
        accessToken: session.access_token,
      });

      if (!res || !isMountedRef.current) return;

      setRemainingUsage(res.credits);
      setOwnerGeneralLimits(res.limits);


      // Web app - no electron API needed
      // Credits info is stored in React state

      // Only set isLoading to false on first load
      if (isLoading) {
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Failed to fetch credits:", error);
      if (isMountedRef.current && isLoading) {
        setIsLoading(false);
      }
    }
  };

  // Manual refresh function to be called from components
  const refreshCredits = async () => {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    await fetchCredits();
  };

  useEffect(() => {
    fetchCredits();

    // Set up interval for refreshing every 10 minutes
    const intervalId = setInterval(() => {
      refreshCredits();
    }, 10 * 60 * 1000); // 10 minutes in milliseconds

    // Cleanup on unmount
    return () => {
      clearInterval(intervalId);
    };
  }, [supabase]);

  return (
    <RemainingCreditsContext.Provider
      value={{
        usage: remainingUsage ?? ({} as RemainingUsage),
        limits: ownerGeneralLimits ?? ({} as GeneralLimits),
        isLoading,
        refreshCredits,
        incrementOfflineWords,
      }}
    >
      {isLoading ? (
        // Expert minimalist loading screen
        <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
          <div className="flex flex-col items-center">
            {/* Logo or app icon animation */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.5,
                ease: "easeOut",
              }}
            >
              {/* Animated logo placeholder - replace with actual logo */}
              <motion.div
                className="w-12 h-12 bg-gradient-to-br from-sky-400 to-sky-500 rounded-2xl"
                animate={{
                  rotate: [0, 90, 180, 270, 360],
                }}
                transition={{
                  duration: 2,
                  ease: "easeInOut",
                  repeat: Infinity,
                }}
              />

              {/* Pulse ring effect */}
              <motion.div
                className="absolute inset-0 rounded-2xl border-2 border-sky-500"
                initial={{ scale: 1, opacity: 0.5 }}
                animate={{
                  scale: [1, 1.5, 2],
                  opacity: [0.5, 0.2, 0],
                }}
                transition={{
                  duration: 2,
                  ease: "easeOut",
                  repeat: Infinity,
                }}
              />
            </motion.div>

            {/* Loading text */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0.2,
                duration: 0.5,
              }}
              className="mt-6 flex items-center space-x-1"
            >
              <span className="text-sm font-light text-gray-500">Loading</span>
              <div className="flex space-x-0.5">
                <motion.span
                  className="text-gray-400"
                  animate={{ opacity: [0, 1, 0] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: 0,
                  }}
                >
                  .
                </motion.span>
                <motion.span
                  className="text-gray-400"
                  animate={{ opacity: [0, 1, 0] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: 0.3,
                  }}
                >
                  .
                </motion.span>
                <motion.span
                  className="text-gray-400"
                  animate={{ opacity: [0, 1, 0] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: 0.6,
                  }}
                >
                  .
                </motion.span>
              </div>
            </motion.div>
          </div>
        </div>
      ) : (
        children
      )}
    </RemainingCreditsContext.Provider>
  );
};

const getRemainingCredits = async ({
  accessToken,
}: {
  accessToken: string;
}): Promise<{ credits: RemainingUsage; limits: GeneralLimits }> => {
  const backendUrl = envConfig.EXPRESS_BACKEND_URL;

  const response = await fetchWithRetry(`${backendUrl}/api/usage-summary`, {
    headers: {
      "Content-Type": "application/json",
      authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch credits");
  }

  const data = await response.json();
  return data.data;
};

// Hook to use the remaining credits context
export const useRemainingCredits = () => {
  const context = useContext(RemainingCreditsContext);
  if (!context) {
    throw new Error(
      "useRemainingCredits must be used within RemainingCreditsProvider"
    );
  }
  return context;
};
